/**
 * Permission Utilities
 *
 * Functions for checking user permissions based on team role
 */

import { useAuthStore } from '@store/authStore';

export type TeamRole = 'Admin' | 'Regular' | 'ReadOnly';

/**
 * Permission levels in order of access
 * Admin > Regular > ReadOnly
 */
const ROLE_HIERARCHY: Record<TeamRole, number> = {
  Admin: 3,
  Regular: 2,
  ReadOnly: 1,
};

/**
 * Check if the current user has one of the allowed roles
 */
export function hasRole(allowedRoles: TeamRole[]): boolean {
  const { teamRole } = useAuthStore.getState();
  if (!teamRole) return false;
  return allowedRoles.includes(teamRole as TeamRole);
}

/**
 * Check if the current user is an Admin
 */
export function isAdmin(): boolean {
  const { teamRole } = useAuthStore.getState();
  return teamRole === 'Admin';
}

/**
 * Check if the current user can edit (Admin or Regular)
 */
export function canEdit(): boolean {
  return hasRole(['Admin', 'Regular']);
}

/**
 * Check if the current user is read-only
 */
export function isReadOnly(): boolean {
  const { teamRole } = useAuthStore.getState();
  return teamRole === 'ReadOnly';
}

/**
 * Check if a role has at least the minimum required access level
 */
export function hasMinimumRole(minimumRole: TeamRole): boolean {
  const { teamRole } = useAuthStore.getState();
  if (!teamRole) return false;

  const currentLevel = ROLE_HIERARCHY[teamRole as TeamRole] || 0;
  const requiredLevel = ROLE_HIERARCHY[minimumRole];

  return currentLevel >= requiredLevel;
}

/**
 * Hook-friendly version that returns permission functions
 * Use this when you need reactive updates
 */
export function usePermissions() {
  const { teamRole } = useAuthStore();

  const currentRole = teamRole as TeamRole | null;

  return {
    role: currentRole,
    isAdmin: currentRole === 'Admin',
    canEdit: currentRole === 'Admin' || currentRole === 'Regular',
    isReadOnly: currentRole === 'ReadOnly',
    hasRole: (roles: TeamRole[]) => currentRole ? roles.includes(currentRole) : false,
    hasMinimumRole: (minRole: TeamRole) => {
      if (!currentRole) return false;
      const currentLevel = ROLE_HIERARCHY[currentRole] || 0;
      const requiredLevel = ROLE_HIERARCHY[minRole];
      return currentLevel >= requiredLevel;
    },
  };
}
